import {
  ORDER_PRIORITY_TIMEFRAME,
  type OrderPriorityTimeframe,
  orderPriorityGroupLabel,
  PRIORITY_TIMEFRAME_OPTIONS,
} from '@seen/core/models/orders/constants'
import { Button, type ButtonProps, cn } from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import { forwardRef, type JSX, type ReactNode, useMemo } from 'react'
import {
  Combobox,
  type ComboboxOption,
  type ComboboxProps,
} from '#src/components/core/combobox/Combobox.tsx'
import { OrderPriorityIcon } from '#src/components/order/OrderPriorityIcon.tsx'

type Priority = OrderPriorityTimeframe | null

interface OrderPriorityComboboxProps extends Omit<
  ComboboxProps<Priority>,
  'options'
> {
  selected: Priority
}

export function OrderPriorityCombobox({
  selected,
  children,
  ...delegated
}: OrderPriorityComboboxProps): JSX.Element {
  const options: ComboboxOption<Priority>[] = useMemo(
    () => [
      {
        value: null,
        label: (
          <OrderPriorityOption
            priority={null}
            isSelected={selected === null || selected === undefined}
          />
        ),
        keywords: ['No Priority', 'none', 'clear'],
      },
      ...PRIORITY_TIMEFRAME_OPTIONS.map((opt) => ({
        value: opt.value,
        label: (
          <OrderPriorityOption
            priority={opt.value}
            isSelected={selected === opt.value}
          />
        ),
        keywords: [opt.label, opt.priority],
      })),
    ],
    [selected]
  )
  return (
    <Combobox options={options} {...delegated}>
      {children}
    </Combobox>
  )
}

export function OrderPriorityOption({
  priority,
  isSelected,
}: {
  priority: OrderPriorityTimeframe | null
  isSelected: boolean
}): JSX.Element {
  const groupLabel = orderPriorityGroupLabel(priority)
  const timeframeLabel =
    priority === null
      ? undefined
      : PRIORITY_TIMEFRAME_OPTIONS.find((opt) => opt.value === priority)?.label

  return (
    <div className="flex flex-1 items-center justify-between">
      <div className="ml-0.5 flex items-center gap-2">
        <OrderPriorityIcon priority={priority} className="shrink-0" />
        <div className="flex items-center gap-1">
          <span>{groupLabel}</span>
          {timeframeLabel && (
            <span className="text-xs text-muted-foreground">
              {timeframeLabel}
            </span>
          )}
        </div>
      </div>
      <IconCheck
        className={cn(
          'mr-0.5 size-4 shrink-0 opacity-0',
          isSelected && 'opacity-100'
        )}
      />
    </div>
  )
}

export const OrderPriorityButton = forwardRef<
  HTMLButtonElement,
  { priority: OrderPriorityTimeframe | null; children?: ReactNode } & Omit<
    ButtonProps,
    'children'
  >
>(({ priority, children, ...props }, ref) => {
  const groupLabel = orderPriorityGroupLabel(priority)
  const timeframe =
    priority === null
      ? undefined
      : PRIORITY_TIMEFRAME_OPTIONS.find((opt) => opt.value === priority)?.label

  return (
    <Button
      ref={ref}
      variant="outline"
      role="combobox"
      className="w-full justify-start gap-2"
      {...props}
    >
      <OrderPriorityIcon priority={priority} className="shrink-0" />
      <span className="flex-1 text-left">
        {groupLabel}
        {timeframe && (
          <span className="text-muted-foreground"> - {timeframe}</span>
        )}
      </span>
      {children}
    </Button>
  )
})

export function orderPriorityTooltipLabel(
  priority: OrderPriorityTimeframe | null | undefined
): string {
  if (!priority) {
    return 'No Priority'
  }
  const groupLabel = orderPriorityGroupLabel(priority)
  const timeframe =
    ORDER_PRIORITY_TIMEFRAME[priority as keyof typeof ORDER_PRIORITY_TIMEFRAME]
      ?.label
  return timeframe ? `${groupLabel}: ${timeframe}` : groupLabel
}

export const OrderPriorityIconButton = forwardRef<
  HTMLButtonElement,
  { priority: OrderPriorityTimeframe | null; children?: ReactNode } & Omit<
    ButtonProps,
    'children'
  >
>(({ priority, children, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      variant="unstyled"
      role="combobox"
      className="h-full p-0! hover:brightness-hover-icon data-[state=open]:brightness-hover-icon"
      {...props}
    >
      <OrderPriorityIcon
        priority={priority}
        tooltip={orderPriorityTooltipLabel(priority)}
      />
      {children}
    </Button>
  )
})
